// src/components/Header.tsx

import React, { useContext, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyIcon from '@mui/icons-material/VpnKey';
import LogoutIcon from '@mui/icons-material/Logout';
import FeedbackIcon from '@mui/icons-material/Feedback';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const StyledAppBar = styled(AppBar)({
  background: 'linear-gradient(to right, #0D47A1, #1565C0)',
  boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
});

const StyledButton = styled(Button)<{
  component?: React.ElementType<any>;
  to?: string;
  href?: string;
  target?: string;
  rel?: string;
}>(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'capitalize',
  color: '#FFFFFF',
  borderRadius: '20px',
  marginLeft: theme.spacing(2),
  padding: '0.5rem 1.2rem',
  transition: 'all 0.3s ease',
  background: 'transparent',
  '&:hover': {
    background: 'rgba(255,255,255,0.1)',
    color: '#E3F2FD',
    boxShadow: '0 0 10px rgba(227,242,253,0.5)',
  },
}));

const LogoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  cursor: 'pointer',
  transition: 'transform 0.3s ease',
  '& img': {
    height: '60px',
    [theme.breakpoints.up('sm')]: {
      height: '70px',
    },
  },
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleApiKeys = () => {
    navigate('/add-api-keys');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  /** Render the common nav items (used in both desktop and mobile) */
  const renderNavItems = () => {
    if (isAuthenticated) {
      return (
          <>
            <StyledButton component={RouterLink} to="/reports">
              Analyst Agent
            </StyledButton>
            <StyledButton component={RouterLink} to="/chat">
              Chat Agent
            </StyledButton>
            <StyledButton
                component="a"
                href="https://news.shmoney.ai"
                target="_blank"
                rel="noopener noreferrer"
            >
              News
            </StyledButton>
            <StyledButton component={RouterLink} to="/feedback">
              Contact
            </StyledButton>
            <Tooltip title="Manage API Keys">
              <IconButton color="inherit" onClick={handleApiKeys} sx={{ ml: 2 }}>
                <KeyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Logout">
              <IconButton color="inherit" onClick={handleLogout} sx={{ ml: 2 }}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
            <IconButton color="inherit" sx={{ ml: 2 }}>
              <AccountCircleIcon />
            </IconButton>
          </>
      );
    }
    return (
        <>
          <StyledButton
              component="a"
              href="https://news.shmoney.ai"
              target="_blank"
              rel="noopener noreferrer"
          >
            News
          </StyledButton>
          <StyledButton component={RouterLink} to="/login">
            Login
          </StyledButton>
          <StyledButton
              component={RouterLink}
              to="/signup"
              sx={{
                ml: 2,
                background: 'linear-gradient(to right, #00e5ff, #00bfa5)',
                boxShadow: '0 4px 15px rgba(0,229,255,0.4)',
                '&:hover': {
                  background: 'linear-gradient(to right, #00bfa5, #00e5ff)',
                  boxShadow: '0 4px 20px rgba(0,229,255,0.5)',
                },
              }}
          >
            Sign Up
          </StyledButton>
        </>
    );
  };

  /** Drawer content (only shown on mobile) */
  const drawerContent = (
      <Box sx={{ width: 250, p: 2 }}>
        <IconButton onClick={toggleDrawer(false)} sx={{ mb: 2 }}>
          <CloseIcon />
        </IconButton>
        <List>
          {isAuthenticated ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/reports">
                    <ListItemText primary="Analyst Agent" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/chat">
                    <ListItemText primary="Chat Agent" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                      component="a"
                      href="https://news.shmoney.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                  >
                    <ListItemText primary="News" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/feedback">
                    <ListItemText primary="Contact" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleApiKeys}>
                    <ListItemIcon>
                      <KeyIcon />
                    </ListItemIcon>
                    <ListItemText primary="API Keys" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemIcon>
                      <AccountCircleIcon />
                    </ListItemIcon>
                    <ListItemText primary="My Account" />
                  </ListItemButton>
                </ListItem>
              </>
          ) : (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                      component="a"
                      href="https://news.shmoney.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                  >
                    <ListItemText primary="News" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/login">
                    <ListItemText primary="Login" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/signup">
                    <ListItemText primary="Sign Up" />
                  </ListItemButton>
                </ListItem>
              </>
          )}
        </List>
      </Box>
  );

  return (
      <StyledAppBar position="static">
        <Toolbar>
          <LogoBox onClick={() => navigate('/')}>
            <img src="/shmoneyai.png" alt="Shmoney AI Logo" />
          </LogoBox>

          {/* Show hamburger icon on mobile; show nav items on desktop */}
          {isMobile ? (
              <IconButton
                  color="inherit"
                  edge="end"
                  onClick={toggleDrawer(true)}
                  sx={{ ml: 'auto' }}
              >
                <MenuIcon />
              </IconButton>
          ) : (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                {renderNavItems()}
              </Box>
          )}

          {/* Drawer for mobile nav */}
          <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
            {drawerContent}
          </Drawer>
        </Toolbar>
      </StyledAppBar>
  );
};

export default Header;
